import qs from "qs";
import { AuthResponse } from "../../types";
import { MultiCalcResponse } from "../../types/analyzer";
import { analyzerApi } from "../api";

export const authAnalyzer = async (
  username: string,
  password: string
): Promise<AuthResponse> => {
  const { data } = await analyzerApi.post(
    "/api/v1/auth/token",
    qs.stringify({
      username,
      password,
    }),
    {
      headers: { "content-type": "application/x-www-form-urlencoded" },
    }
  );
  return data;
};

// old implementation of multi_calculation api
export const getMultiCalcLegacy = async (
  token: string,
  input: { [key: string]: number } = {}
): Promise<MultiCalcResponse | null> => {
  const dates = Object.keys(input);
  if (!dates.length) return null;
  const { data } = await analyzerApi.post(
    "/api/v1/calculation/multi_calculation",
    {
      calculus_definitions: [
        {
          func: "specific_returns",
          index_id: -1,
          time_periods: ["YTD", "1Y", "3Y", "5Y", "max"],
          start_date: dates[0],
          end_date: dates[dates.length - 1],
        },
        {
          func: "annualized_returns",
          index_id: -1,
          time_periods: ["YTD", "1Y", "3Y", "5Y", "max"],
          start_date: dates[0],
          end_date: dates[dates.length - 1],
        },
        {
          func: "annualized_sharpe_ratio",
          index_id: -1,
          time_periods: ["YTD", "1Y", "3Y", "5Y", "max"],
          start_date: dates[0],
          end_date: dates[dates.length - 1],
        },
        {
          func: "specific_volatility",
          index_id: -1,
          time_periods: ["YTD", "1Y", "3Y", "5Y", "max"],
          start_date: dates[0],
          end_date: dates[dates.length - 1],
        },
        {
          func: "annualized_specific_volatility",
          index_id: -1,
          time_periods: ["YTD", "1Y", "3Y", "5Y", "max"],
          start_date: dates[0],
          end_date: dates[dates.length - 1],
        },
        {
          func: "max_drawdown",
          index_id: -1,
          time_periods: ["YTD", "1Y", "3Y", "5Y", "max"],
          start_date: dates[0],
          end_date: dates[dates.length - 1],
        },
      ],
      custom_data: [
        {
          field_id: -1,
          format: "time_series",
          field_type: "index_values",
          data: input,
        },
      ],
    },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return data;
};

// Utility function to map the new response structure
export const mapMultiCalcResponse = (response: MultiCalcResponse) => {
  const mapped = {
    returns: {
      "1Y": 0,
      "3Y": 0,
      "5Y": 0,
      YTD: 0,
      max: 0,
    },
    annualizedReturns: {
      "1Y": 0,
      "3Y": 0,
      "5Y": 0,
      YTD: 0,
      max: 0,
    },
    sharpeRatio: {
      "1Y": 0,
      "3Y": 0,
      "5Y": 0,
      YTD: 0,
      max: 0,
    },
    dailyVolatility: {
      "1Y": 0,
      "3Y": 0,
      "5Y": 0,
      YTD: 0,
      max: 0,
    },
    annualizedVolatility: {
      "1Y": 0,
      "3Y": 0,
      "5Y": 0,
      YTD: 0,
      max: 0,
    },
    maxDrawdown: {
      "1Y": { max_drawdown: 0, high_date: "", low_date: "", high_value: 0, low_value: 0 },
      "3Y": { max_drawdown: 0, high_date: "", low_date: "", high_value: 0, low_value: 0 },
      "5Y": { max_drawdown: 0, high_date: "", low_date: "", high_value: 0, low_value: 0 },
      YTD: { max_drawdown: 0, high_date: "", low_date: "", high_value: 0, low_value: 0 },
      max: { max_drawdown: 0, high_date: "", low_date: "", high_value: 0, low_value: 0 },
    },
  };

  Object.entries(response.result).forEach(([key, value]) => {
    const result = value.result;
    
    if (key.includes("/index_returns/return/")) {
      const period = key.split("/").pop() as keyof typeof mapped.returns;
      if (typeof result === "number") {
        mapped.returns[period] = result;
      }
    } else if (key.includes("/index_returns/annualized_return/")) {
      const period = key.split("/").pop() as keyof typeof mapped.annualizedReturns;
      if (typeof result === "number") {
        mapped.annualizedReturns[period] = result;
      }
    } else if (key.includes("/sharpe_ratio/")) {
      const period = key.split("/").pop() as keyof typeof mapped.sharpeRatio;
      if (typeof result === "number") {
        mapped.sharpeRatio[period] = result;
      }
    } else if (key.includes("/daily_volatility/")) {
      const period = key.split("/").pop() as keyof typeof mapped.dailyVolatility;
      if (typeof result === "number") {
        mapped.dailyVolatility[period] = result;
      }
    } else if (key.includes("/annualized_volatility/")) {
      const period = key.split("/").pop() as keyof typeof mapped.annualizedVolatility;
      if (typeof result === "number") {
        mapped.annualizedVolatility[period] = result;
      }
    } else if (key.includes("/max_drawdown/")) {
      const period = key.split("/").pop() as keyof typeof mapped.maxDrawdown;
      if (typeof result === "object" && result !== null) {
        mapped.maxDrawdown[period] = result as any;
      }
    }
  });

  return mapped;
};

export const getMultiCalc = async (
  token: string,
  input: { [key: string]: number } = {}
): Promise<MultiCalcResponse | null> => {
  const dates = Object.keys(input);
  if (!dates.length) return null;

  const { data } = await analyzerApi.post(
    "/api/v1/calculation/multi_calculation",
    {
      calculus_definitions: {
        "index/performance_and_risk/general_statistics/index_returns/return/1Y": {
          time_period: "1Y",
          resample_func: "last",
          calculate_on: "index",
          func: "returns",
          calculation_frequency: "daily"
        },
        "index/performance_and_risk/general_statistics/index_returns/return/3Y": {
          time_period: "3Y",
          resample_func: "last",
          calculate_on: "index",
          func: "returns",
          calculation_frequency: "daily"
        },
        "index/performance_and_risk/general_statistics/index_returns/return/5Y": {
          time_period: "5Y",
          resample_func: "last",
          calculate_on: "index",
          func: "returns",
          calculation_frequency: "daily"
        },
        "index/performance_and_risk/general_statistics/index_returns/return/max": {
          time_period: "max",
          resample_func: "last",
          calculate_on: "index",
          func: "returns",
          calculation_frequency: "daily"
        },
        "index/performance_and_risk/general_statistics/index_returns/return/YTD": {
          time_period: "YTD",
          resample_func: "last",
          calculate_on: "index",
          func: "returns",
          calculation_frequency: "daily"
        },
        "index/performance_and_risk/general_statistics/index_returns/annualized_return/1Y": {
          time_period: "1Y",
          resample_func: "last",
          calculate_on: "index",
          func: "returns",
          calculation_frequency: "annualized"
        },
        "index/performance_and_risk/general_statistics/index_returns/annualized_return/3Y": {
          time_period: "3Y",
          resample_func: "last",
          calculate_on: "index",
          func: "returns",
          calculation_frequency: "annualized"
        },
        "index/performance_and_risk/general_statistics/index_returns/annualized_return/5Y": {
          time_period: "5Y",
          resample_func: "last",
          calculate_on: "index",
          func: "returns",
          calculation_frequency: "annualized"
        },
        "index/performance_and_risk/general_statistics/index_returns/annualized_return/max": {
          time_period: "max",
          resample_func: "last",
          calculate_on: "index",
          func: "returns",
          calculation_frequency: "annualized"
        },
        "index/performance_and_risk/general_statistics/index_returns/annualized_return/YTD": {
          time_period: "YTD",
          resample_func: "last",
          calculate_on: "index",
          func: "returns",
          calculation_frequency: "annualized"
        },
        "comparison/performance_and_risk/general_statistics/index_returns/sharpe_ratio/1Y": {
          time_period: "1Y",
          resample_func: "last",
          calculate_on: "index",
          func: "sharpe_ratio",
          calculation_frequency: "annualized"
        },
        "comparison/performance_and_risk/general_statistics/index_returns/sharpe_ratio/3Y": {
          time_period: "3Y",
          resample_func: "last",
          calculate_on: "index",
          func: "sharpe_ratio",
          calculation_frequency: "annualized"
        },
        "comparison/performance_and_risk/general_statistics/index_returns/sharpe_ratio/5Y": {
          time_period: "5Y",
          resample_func: "last",
          calculate_on: "index",
          func: "sharpe_ratio",
          calculation_frequency: "annualized"
        },
        "comparison/performance_and_risk/general_statistics/index_returns/sharpe_ratio/max": {
          time_period: "max",
          resample_func: "last",
          calculate_on: "index",
          func: "sharpe_ratio",
          calculation_frequency: "annualized"
        },
        "comparison/performance_and_risk/general_statistics/index_returns/sharpe_ratio/YTD": {
          time_period: "YTD",
          resample_func: "last",
          calculate_on: "index",
          func: "sharpe_ratio",
          calculation_frequency: "annualized"
        },
        "index/performance_and_risk/general_statistics/index_volatility/daily_volatility/1Y": {
          time_period: "1Y",
          resample_func: "last",
          calculate_on: "index",
          func: "volatility",
          calculation_frequency: "daily"
        },
        "index/performance_and_risk/general_statistics/index_volatility/daily_volatility/3Y": {
          time_period: "3Y",
          resample_func: "last",
          calculate_on: "index",
          func: "volatility",
          calculation_frequency: "daily"
        },
        "index/performance_and_risk/general_statistics/index_volatility/daily_volatility/5Y": {
          time_period: "5Y",
          resample_func: "last",
          calculate_on: "index",
          func: "volatility",
          calculation_frequency: "daily"
        },
        "index/performance_and_risk/general_statistics/index_volatility/daily_volatility/max": {
          time_period: "max",
          resample_func: "last",
          calculate_on: "index",
          func: "volatility",
          calculation_frequency: "daily"
        },
        "index/performance_and_risk/general_statistics/index_volatility/daily_volatility/YTD": {
          time_period: "YTD",
          resample_func: "last",
          calculate_on: "index",
          func: "volatility",
          calculation_frequency: "daily"
        },
        "index/performance_and_risk/general_statistics/index_volatility/annualized_volatility/1Y": {
          time_period: "1Y",
          resample_func: "last",
          calculate_on: "index",
          func: "volatility",
          calculation_frequency: "annualized"
        },
        "index/performance_and_risk/general_statistics/index_volatility/annualized_volatility/3Y": {
          time_period: "3Y",
          resample_func: "last",
          calculate_on: "index",
          func: "volatility",
          calculation_frequency: "annualized"
        },
        "index/performance_and_risk/general_statistics/index_volatility/annualized_volatility/5Y": {
          time_period: "5Y",
          resample_func: "last",
          calculate_on: "index",
          func: "volatility",
          calculation_frequency: "annualized"
        },
        "index/performance_and_risk/general_statistics/index_volatility/annualized_volatility/max": {
          time_period: "max",
          resample_func: "last",
          calculate_on: "index",
          func: "volatility",
          calculation_frequency: "annualized"
        },
        "index/performance_and_risk/general_statistics/index_volatility/annualized_volatility/YTD": {
          time_period: "YTD",
          resample_func: "last",
          calculate_on: "index",
          func: "volatility",
          calculation_frequency: "annualized"
        },
        "index/performance_and_risk/general_statistics/index_volatility/max_drawdown/1Y": {
          time_period: "1Y",
          resample_func: "last",
          calculate_on: "index",
          func: "max_drawdown"
        },
        "index/performance_and_risk/general_statistics/index_volatility/max_drawdown/3Y": {
          time_period: "3Y",
          resample_func: "last",
          calculate_on: "index",
          func: "max_drawdown"
        },
        "index/performance_and_risk/general_statistics/index_volatility/max_drawdown/5Y": {
          time_period: "5Y",
          resample_func: "last",
          calculate_on: "index",
          func: "max_drawdown"
        },
        "index/performance_and_risk/general_statistics/index_volatility/max_drawdown/max": {
          time_period: "max",
          resample_func: "last",
          calculate_on: "index",
          func: "max_drawdown"
        },
        "index/performance_and_risk/general_statistics/index_volatility/max_drawdown/YTD": {
          time_period: "YTD",
          resample_func: "last",
          calculate_on: "index",
          func: "max_drawdown"
        }
      },
      index: {
        index_type: "custom",
        index_values: input,
      },
    },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return data;
};
